import type { LangChainMessage } from "@assistant-ui/react-langgraph";

import { asRecord } from "@/agents-v2/utils/unknown";

/**
 * The id of the prompt recipe a message was sent from, carried to the backend as
 * human-message metadata. It tells the agent which curated flow the user chose,
 * rather than leaving the backend to infer intent from the prompt wording (which
 * is localised, and so unreliable as a signal).
 *
 * Read by ``ScoringEnforcementMiddleware`` on the backend; keep
 * {@link SCORE_RESPONSE_RECIPE_ID} in sync with its ``SCORE_RESPONSE_RECIPE_ID``.
 */
export const AGENT_RECIPE_ID_METADATA_KEY = "agent_recipe_id";
export const SCORE_RESPONSE_RECIPE_ID = "score-response";

type RecipeIdListener = () => void;

const recipeIdListeners = new Set<RecipeIdListener>();
let currentRecipeId: string | null = null;

const findLastHumanMessageIndex = (messages: LangChainMessage[]): number => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].type === "human") return index;
  }
  return -1;
};

export const getRecipeId = () => currentRecipeId;

export const publishRecipeId = (recipeId: string | null) => {
  currentRecipeId = recipeId?.trim() ? recipeId.trim() : null;
  recipeIdListeners.forEach((listener) => listener());
};

export const subscribeRecipeId = (listener: RecipeIdListener) => {
  recipeIdListeners.add(listener);
  return () => recipeIdListeners.delete(listener);
};

/**
 * Attach a recipe id to the latest human message, preserving any metadata
 * already on it (such as the selected-text reference).
 * @param messages - The outgoing conversation messages.
 * @param recipeId - The recipe id to attach, or null to leave messages as they are.
 * @returns The messages, with the recipe id attached to the last human message.
 */
export const attachRecipeId = (
  messages: LangChainMessage[],
  recipeId: string | null
): LangChainMessage[] => {
  if (!recipeId) return messages;
  const humanIndex = findLastHumanMessageIndex(messages);
  if (humanIndex < 0) return messages;

  const humanMessage = messages[humanIndex];
  if (humanMessage.type !== "human") return messages;
  const additionalKwargs = humanMessage.additional_kwargs ?? {};
  const metadata = asRecord(additionalKwargs.metadata);
  const nextMessages = [...messages];
  nextMessages[humanIndex] = {
    ...humanMessage,
    additional_kwargs: {
      ...additionalKwargs,
      metadata: {
        ...metadata,
        [AGENT_RECIPE_ID_METADATA_KEY]: recipeId
      }
    }
  };
  return nextMessages;
};
